/**
 * HOC for the v2 provisional-forecast routes.
 *
 * Mirrors the slim shape of `SalesPlanUpdatedHoc`: just a Navbar + an
 * Outlet. Search lives inside the Landing screen (NOT in the Navbar)
 * because the v2 entry experience is "centered search field on an
 * empty page" — putting the search in the Navbar would compete with
 * that. The sidebar is rendered from the Landing screen too, so the
 * HOC stays decoration-free.
 *
 * Resets the v2 store on unmount so navigating away doesn't leave
 * stale suggestion lists or sidebar state behind.
 */

import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { observer } from 'mobx-react';

import Navbar from 'components/navbar';
import { useStore } from 'hooks/useStore';

const ProvisionalForecastV2Hoc = () => {
  const {
    ProvisionalForecastV2Store: { resetStore },
  } = useStore();

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, [resetStore]);

  return (
    <div>
      <Navbar
        heading="FDP - Provisional Forecast (v2)"
        // Navbar uses `justify-content: space-between` on the right
        // half, so children there get evenly spaced. When we pass
        // an empty fragment for `searchComponent`, the slot that
        // other screens fill with a search bar collapses — and
        // "Export All" (rendered globally for every
        // `provisonal_forecast` route) ends up centred in the
        // navbar. Filling `searchComponent` with a flex-grow spacer
        // absorbs that slack so the buttons right-align.
        //
        // `progressComponent` lives between Export All and the
        // right cluster (websocket indicator / bell / avatar) in
        // the navbar template. We use it as a small horizontal
        // spacer so the Export All button doesn't kiss the status
        // indicator. Both inserts are local to v2; the global
        // Navbar component is unchanged.
        searchComponent={<div style={{ flexGrow: 1 }} />}
        progressComponent={<div style={{ width: '1rem' }} />}
      />
      <Outlet />
    </div>
  );
};

export default observer(ProvisionalForecastV2Hoc);
